import React, { useState, useEffect, useRef } from "react";
import {
    Form,
    FormGroup,
    Label,
    Input,
    Button,
    Row,
    Col,
    ListGroup,
    ListGroupItem,
    FormFeedback,
} from "reactstrap";
import Session from "../auth/Session";

const normalizeState = (value) => String(value || "").trim().toUpperCase();

const getDriverStates = (driver) => {
    const states = [
        ...(driver?.states || []),
        ...(driver?.stateAssignments || []).map(a => a.state),
    ];

    if (!states.length && driver?.state) {
        states.push(driver.state);
    }

    return [...new Set(states.map(normalizeState).filter(Boolean))];
};

const driverMatchesState = (driver, state) =>
    getDriverStates(driver).includes(normalizeState(state));

const SubCustomerForm = ({ subCustomer, apiBaseUrl, onSuccess, onCancel }) => {
    const [formData, setFormData] = useState({
        id: 0,
        customerId: "",
        qbCustomerId: null,
        name: "",
        designation: "",
        address1: "",
        address2: "",
        city: "",
        state: "",
        postalCode: "",
        contactName: "",
        contactEmail: "",
        invoiceNumber: "",
        poNumber: "",
        warehouseNumber: "",
        falseRouteInvoiceRate: 0,
        falseRouteDriverRate: 0,
        backupDriverId: null,
        backupInvoiceRate: 0,
        backupDriverRate: 0,
        leadDriverId: null,
        leadManagementDriverRate: 0,
        leadManagementInvoiceRate: 0,
        invoiceLeadManagementPay: false,
        managerId: "",
        isActive: true,
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);

    const [customers, setCustomers] = useState([]);
    const [customerSearch, setCustomerSearch] = useState("");
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustomerList, setShowCustomerList] = useState(false);

    const [users, setUsers] = useState([]);
    const [userSearch, setUserSearch] = useState("");
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [showUserList, setShowUserList] = useState(false);

    const [drivers, setDrivers] = useState([]);

    const customerApi = "/api/customers";
    const usersApi = "/api/users";
    const driversApi = "/api/drivers/list";

    const customerDropdownRef = useRef(null);
    const userDropdownRef = useRef(null);

    useEffect(() => {
        if (subCustomer) setFormData(subCustomer);
    }, [subCustomer]);

    useEffect(() => {
        const loadData = async () => {
            const token = await Session.getToken();
            const res = await fetch(customerApi, { headers: { Authorization: `Bearer ${token}` } });
            if (res.ok) {
                let data = await res.json();
                setCustomers(data);
            }
            const res2 = await fetch(usersApi, { headers: { Authorization: `Bearer ${token}` } });
            if (res2.ok) {
                let data = await res2.json();
                setUsers(data);
            }
            const res3 = await fetch(driversApi, { headers: { Authorization: `Bearer ${token}` } });
            if (res3.ok) {
                let data = await res3.json();
                setDrivers(data);
            }
        }
        loadData();
    }, []);

    useEffect(() => {
        setFilteredCustomers(
            customerSearch
                ? customers.filter((c) =>
                    c.name.toLowerCase().includes(customerSearch.toLowerCase())
                )
                : []
        );
    }, [customerSearch, customers]);

    useEffect(() => {
        setFilteredUsers(
            userSearch
                ? users.filter(
                    (u) =>
                        u.userName.toLowerCase().includes(userSearch.toLowerCase()) ||
                        `${u.firstName} ${u.lastName}`
                            .toLowerCase()
                            .includes(userSearch.toLowerCase())
                )
                : []
        );
    }, [userSearch, users]);

    const validate = () => {
        const newErrors = {};
        if (!formData.name) newErrors.name = "Name required";
        if (!formData.contactEmail) newErrors.contactEmail = "Email required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSelectCustomer = (c) => {
        setFormData((p) => ({ ...p, customerId: c.id }));
        setCustomerSearch(c.name);
        setShowCustomerList(false);
    };

    const handleSelectManager = (u) => {
        setFormData((p) => ({ ...p, managerId: u.id }));
        setUserSearch(`${u.firstName} ${u.lastName} (${u.userName})`);
        setShowUserList(false);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setLoading(true);
        try {
            const method = subCustomer ? "PUT" : "POST";
            const url = subCustomer
                ? `${apiBaseUrl}/${formData.id}`
                : apiBaseUrl;
            const token = await Session.getToken();
            const res = await fetch(url, {
                method,
                headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error("Save failed");
            const result = await res.json();
            onSuccess(result);
        } catch (err) {
            alert(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit}>
            {/* Identity */}
            <Row>
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Name</Label>
                        <Input
                            bsSize="sm"
                            name="name"
                            value={formData.name}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, name: e.target.value }))
                            }
                            invalid={!!errors.name}
                        />
                        <FormFeedback>{errors.name}</FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Designation</Label>
                        <Input
                            bsSize="sm"
                            name="designation"
                            value={formData.designation}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, designation: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label for="customerId" className="mb-1">Customer</Label>
                        <Input
                            bsSize="sm"
                            id="customerId"
                            name="customerId"
                            type="select"
                            value={formData.customerId}
                            onChange={(e) => {
                                const { name, value } = e.target;
                                setFormData((p) => ({ ...p, customerId: value }))
                            }}
                        >
                            <option value="">-- Select Customer --</option>
                            {customers.map(sc => (
                                <option key={sc.id} value={sc.id}>
                                    {sc.name} {sc.customer ? `— ${sc.customer.name}` : ""}
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.customerId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">QB Customer Id</Label>
                        <Input
                            bsSize="sm"
                            name="qbCustomerId"
                            type="text"
                            value={formData.qbCustomerId ?? ""}
                            onChange={(e) => {
                                    if (e.target.value) {
                                        setFormData((p) => ({ ...p, qbCustomerId: e.target.value }));
                                    } else {
                                        setFormData((p) => ({ ...p, qbCustomerId: null }));
                                    }
                                }
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Warehouse #</Label>
                        <Input
                            bsSize="sm"
                            name="warehouseNumber"
                            value={formData.warehouseNumber || ""}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, warehouseNumber: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="mb-2" ref={userDropdownRef}>
                        <Label for="managerId" className="mb-1">Manager</Label>
                        <Input
                            bsSize="sm"
                            id="managerId"
                            name="managerId"
                            type="select"
                            value={formData.managerId}
                            onChange={(e) => {
                                const { name, value } = e.target;
                                setFormData((p) => ({ ...p, managerId: value }))
                            }}
                        >
                            <option value="">-- Select Manager --</option>
                            {users.map(manager => (
                                <option key={manager.id} value={manager.id}>
                                    {manager.firstName} {manager.lastName} - ({manager.userName})
                                </option>
                            ))}
                        </Input>
                        <FormFeedback>{errors.managerId}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            {/* Address */}
            <Row>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Address 1</Label>
                        <Input
                            bsSize="sm"
                            name="address1"
                            value={formData.address1}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, address1: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Address 2</Label>
                        <Input
                            bsSize="sm"
                            name="address2"
                            value={formData.address2}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, address2: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            <Row>
                <Col md={5}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">City</Label>
                        <Input bsSize="sm" name="city"
                            value={formData.city}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, city: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={3}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">State</Label>
                        <Input bsSize="sm" name="state"
                            value={formData.state}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, state: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Postal Code</Label>
                        <Input
                            bsSize="sm"
                            name="postalCode"
                            value={formData.postalCode}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, postalCode: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            {/* Contact */}
            <Row>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Contact Name</Label>
                        <Input
                            bsSize="sm"
                            name="contactName"
                            value={formData.contactName || ""}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, contactName: e.target.value }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Contact Email</Label>
                        <Input
                            bsSize="sm"
                            type="email"
                            name="contactEmail"
                            value={formData.contactEmail || ""}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, contactEmail: e.target.value }))
                            }
                            invalid={!!errors.contactEmail}
                        />
                        <FormFeedback>{errors.contactEmail}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            {/* Rates */}
            <Row>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">False Trip Invoice Rate</Label>
                        <Input
                            bsSize="sm"
                            name="falseRouteInvoiceRate"
                            type="number"
                            step="0.01"
                            value={formData.falseRouteInvoiceRate ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, falseRouteInvoiceRate: parseFloat(e.target.value) || 0 }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">False Trip Driver Rate</Label>
                        <Input
                            bsSize="sm"
                            name="falseRouteDriverRate"
                            type="number"
                            step="0.01"
                            value={formData.falseRouteDriverRate ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, falseRouteDriverRate: parseFloat(e.target.value) || 0 }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Backup Invoice Rate</Label>
                        <Input
                            bsSize="sm"
                            name="backupInvoiceRate"
                            type="number"
                            step="0.01"
                            value={formData.backupInvoiceRate ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, backupInvoiceRate: parseFloat(e.target.value) || 0 }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Backup Driver Rate</Label>
                        <Input
                            bsSize="sm"
                            name="backupDriverRate"
                            type="number"
                            step="0.01"
                            value={formData.backupDriverRate ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, backupDriverRate: parseFloat(e.target.value) || 0 }))
                            }
                        />
                    </FormGroup>
                </Col>
            </Row>

            {/* Backup driver + status */}
            <Row className="align-items-end">
                <Col md={8}>
                    <FormGroup className="mb-2">
                        <Label for="backupDriverId" className="mb-1">Backup Driver</Label>
                        <Input
                            bsSize="sm"
                            id="backupDriverId"
                            name="backupDriverId"
                            type="select"
                            value={formData.backupDriverId ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, backupDriverId: e.target.value ? Number(e.target.value) : null }))
                            }
                        >
                            <option value="0">-- No Backup Driver --</option>
                            {drivers.filter(d => driverMatchesState(d, formData.state)).map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup check className="mb-2">
                        <Input
                            type="checkbox"
                            name="isActive"
                            id="isActive"
                            checked={formData.isActive ?? true}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, isActive: e.target.checked }))
                            }
                        />{" "}
                        <Label for="isActive" check>Active</Label>
                    </FormGroup>
                </Col>
            </Row>

            {/* Lead (management) driver: paid on the paysheet; invoiced only when the box is checked. */}
            <Row className="align-items-end">
                <Col md={4}>
                    <FormGroup className="mb-2">
                        <Label for="leadDriverId" className="mb-1">Lead Driver</Label>
                        <Input
                            bsSize="sm"
                            id="leadDriverId"
                            name="leadDriverId"
                            type="select"
                            value={formData.leadDriverId ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, leadDriverId: e.target.value ? Number(e.target.value) : null }))
                            }
                        >
                            <option value="0">-- No Lead Driver --</option>
                            {drivers.filter(d => driverMatchesState(d, formData.state)).map(d => (
                                <option key={d.id} value={d.id}>
                                    {d.driverName}{d.businessName ? ` — ${d.businessName}` : ""}
                                </option>
                            ))}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Lead Management Rate</Label>
                        <Input
                            bsSize="sm"
                            name="leadManagementDriverRate"
                            type="number"
                            step="0.01"
                            value={formData.leadManagementDriverRate ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, leadManagementDriverRate: parseFloat(e.target.value) || 0 }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={3} xs={6}>
                    <FormGroup className="mb-2">
                        <Label className="mb-1">Lead Invoice Rate</Label>
                        <Input
                            bsSize="sm"
                            name="leadManagementInvoiceRate"
                            type="number"
                            step="0.01"
                            value={formData.leadManagementInvoiceRate ?? 0}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, leadManagementInvoiceRate: parseFloat(e.target.value) || 0 }))
                            }
                        />
                    </FormGroup>
                </Col>
                <Col md={2}>
                    <FormGroup check className="mb-2">
                        <Input
                            type="checkbox"
                            name="invoiceLeadManagementPay"
                            id="invoiceLeadManagementPay"
                            checked={formData.invoiceLeadManagementPay ?? false}
                            onChange={(e) =>
                                setFormData((p) => ({ ...p, invoiceLeadManagementPay: e.target.checked }))
                            }
                        />{" "}
                        <Label for="invoiceLeadManagementPay" check>Invoice Lead Pay</Label>
                    </FormGroup>
                </Col>
            </Row>

            <div className="d-flex justify-content-end gap-2 mt-2">
                <Button color="secondary" onClick={onCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button color="primary" type="submit" disabled={loading}>
                    {loading ? "Saving..." : subCustomer ? "Update" : "Create"}
                </Button>
            </div>
        </Form>
    );
};

export default SubCustomerForm;
